import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Radio, RadioGroup, FormControlLabel, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import {
    addProgramDraftWeek,
    clearProgramDraft,
    copyDraftProgramWeek,
    discardDraftProgramChanges,
    loadProgramEditor,
    loadPrograms,
    removeDraftProgramDay,
    saveDraftProgram,
    setProgramSelectedWeek,
    startImportedProgramDraft,
    startNewProgramDraft,
    updateDraftProgramCatalog,
    updateDraftProgramDay,
} from '../../actions/programs';
import { ProgramWeeks, ProgramWorkoutItemRecord } from '../../api/programs.interface';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import UnsavedChangesDialog from '../exercises/UnsavedChangesDialog';
import CopyWeekDialog from './CopyWeekDialog';
import ProgramDayEditorModal from './ProgramDayEditorModal';
import ProgramEditorHeader from './ProgramEditorHeader';
import ProgramImportDialog from './ProgramImportDialog';
import ProgramWeekGrid from './ProgramWeekGrid';
import ProgramWeekNavigator from './ProgramWeekNavigator';
import {
    DEFAULT_COPY_WEEK_VALUES,
    DEFAULT_PROGRAM_CREATE_VALUES,
    createProgramWeekOptions,
    getProgramEmptyDayNumbers,
    getProgramNextWeekNumber,
    getProgramWeekSchedule,
    getProgramWeekStatistics,
    syncProgramCreateValuesWithDisplayName,
    syncProgramCreateValuesWithName,
    validateCopyWeekForm,
    validateProgramCreateForm,
} from './programs.utils';
import { CopyWeekFormErrors, CopyWeekFormValues, ProgramCreateFormErrors, ProgramCreateFormValues, ProgramEditorDraft, ProgramManagementListItem, SaveProgramDraftResult, StartProgramDraftResult } from './programs.types';
import ProgramCatalogFields from './ProgramCatalogFields';

interface ComponentProps {}

interface ReduxProps {
    draftProgram?: ProgramEditorDraft;
    hasUnsavedChanges: boolean;
    items: ProgramManagementListItem[];
    loadingList: boolean;
    loadingProgram: boolean;
    savingProgram: boolean;
    selectedWeek: number;
}

interface ReduxActionProps {
    addProgramDraftWeek: (week: number) => any;
    clearProgramDraft: () => any;
    copyDraftProgramWeek: (sourceWeek: number, targetWeek: number) => any;
    discardDraftProgramChanges: () => any;
    loadProgramEditor: (name: string, preferredWeek?: number) => any;
    loadPrograms: () => any;
    removeDraftProgramDay: (week: number, day: number) => any;
    saveDraftProgram: () => Promise<SaveProgramDraftResult>;
    setProgramSelectedWeek: (week: number) => any;
    startImportedProgramDraft: (name: string, weeks: ProgramWeeks) => any;
    startNewProgramDraft: (values: ProgramCreateFormValues) => Promise<StartProgramDraftResult>;
    updateDraftProgramCatalog: (fieldName: 'displayName' | 'live' | 'weeks', value: boolean | number | string) => any;
    updateDraftProgramDay: (week: number, day: number, item: ProgramWorkoutItemRecord) => any;
}

interface State {
    activeDay?: number;
    copyWeekErrors: CopyWeekFormErrors;
    copyWeekValues: CopyWeekFormValues;
    createErrors: ProgramCreateFormErrors;
    createValues: ProgramCreateFormValues;
    showCopyWeekDialog: boolean;
    showDayEditor: boolean;
    showImportDialog: boolean;
    showUnsavedChangesDialog: boolean;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class EditProgramPage extends Component<Props, State> {
    state: State = {
        copyWeekErrors: {},
        copyWeekValues: DEFAULT_COPY_WEEK_VALUES,
        createErrors: {},
        createValues: DEFAULT_PROGRAM_CREATE_VALUES,
        showCopyWeekDialog: false,
        showDayEditor: false,
        showImportDialog: false,
        showUnsavedChangesDialog: false,
    };

    componentDidMount() {
        this.loadPageData();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    componentDidUpdate(prevProps: Props) {
        const previousRoute = `${prevProps.params.name || 'new'}:${prevProps.params.week || 'none'}`;
        const currentRoute = `${this.props.params.name || 'new'}:${this.props.params.week || 'none'}`;

        if (previousRoute !== currentRoute) {
            this.loadPageData();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    }

    getActiveDayItem() {
        const { activeDay } = this.state;

        if (!activeDay || !this.props.draftProgram) {
            return undefined;
        }

        return this.props.draftProgram.weeks[String(this.props.selectedWeek)]?.[String(activeDay)];
    }

    getCreateSourceWeekOptions() {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    }

    getProgramName() {
        return String(this.props.params.name || '');
    }

    getPreferredWeek() {
        const parsedWeek = Number(this.props.params.week);

        if (!Number.isInteger(parsedWeek) || parsedWeek < 1) {
            return undefined;
        }

        return parsedWeek;
    }

    isDirty() {
        return this.props.hasUnsavedChanges;
    }

    isNewProgramRoute() {
        return !this.props.params.name;
    }

    loadPageData() {
        if (this.isNewProgramRoute()) {
            this.props.clearProgramDraft();
            this.props.loadPrograms();
            this.setState({
                activeDay: undefined,
                copyWeekErrors: {},
                copyWeekValues: DEFAULT_COPY_WEEK_VALUES,
                createErrors: {},
                createValues: DEFAULT_PROGRAM_CREATE_VALUES,
                showCopyWeekDialog: false,
                showDayEditor: false,
                showImportDialog: this.shouldOpenImportDialog(),
                showUnsavedChangesDialog: false,
            });
            return;
        }

        const programName = this.getProgramName();

        if (!programName) {
            return;
        }

        this.props.loadProgramEditor(programName, this.getPreferredWeek());
        this.props.loadPrograms();
        this.setState({
            activeDay: undefined,
            copyWeekErrors: {},
            showCopyWeekDialog: false,
            showDayEditor: false,
            showImportDialog: false,
            showUnsavedChangesDialog: false,
        });
    }

    shouldOpenImportDialog() {
        return new URLSearchParams(this.props.location.search).get('import') === '1';
    }

    onAddDay = (day: number) => {
        this.setState({ activeDay: day, showDayEditor: true });
    };

    onAddDayFromWeekSummary = () => {
        const { draftProgram, selectedWeek } = this.props;

        if (!draftProgram) {
            return;
        }

        const firstEmptyDay = getProgramEmptyDayNumbers(draftProgram.weeks, selectedWeek)[0];

        if (!firstEmptyDay) {
            return;
        }

        this.onAddDay(firstEmptyDay);
    };

    onAddWeek = () => {
        if (!this.props.draftProgram) {
            return;
        }

        const nextWeek = getProgramNextWeekNumber(this.props.draftProgram.weeks);

        this.props.addProgramDraftWeek(nextWeek);
    };

    onBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!this.isDirty()) {
            return undefined;
        }

        event.preventDefault();
        event.returnValue = '';
        return '';
    };

    onCancel = () => {
        if (!this.isDirty()) {
            this.navigateAfterCancel();
            return;
        }

        this.setState({ showUnsavedChangesDialog: true });
    };

    onCloseCopyWeekDialog = () => {
        this.setState({ showCopyWeekDialog: false });
    };

    onCloseImportDialog = () => {
        this.setState({ showImportDialog: false });
    };

    onCloseDayEditor = () => {
        this.setState({ activeDay: undefined, showDayEditor: false });
    };

    onCloseUnsavedChangesDialog = () => {
        this.setState({ showUnsavedChangesDialog: false });
    };

    onConfirmCopyWeek = () => {
        const { draftProgram } = this.props;

        if (!draftProgram) {
            return;
        }

        const copyWeekErrors = validateCopyWeekForm(this.state.copyWeekValues, draftProgram.weeks);

        if (Object.keys(copyWeekErrors).length) {
            this.setState({ copyWeekErrors });
            return;
        }

        this.props.copyDraftProgramWeek(Number(this.state.copyWeekValues.sourceWeek), Number(this.state.copyWeekValues.targetWeek));
        this.setState({ showCopyWeekDialog: false });
    };

    onConfirmDiscardChanges = () => {
        if (this.isNewProgramRoute()) {
            this.props.clearProgramDraft();
        } else {
            this.props.discardDraftProgramChanges();
        }

        this.setState({ showUnsavedChangesDialog: false });
        this.navigateAfterCancel();
    };

    onContinueToBuilder = async () => {
        const createErrors = validateProgramCreateForm(this.state.createValues, this.props.items.map((item) => item.name));

        if (Object.keys(createErrors).length) {
            this.setState({ createErrors });
            return;
        }

        const result = await this.props.startNewProgramDraft(this.state.createValues);

        if (result.success) {
            this.props.setProgramSelectedWeek(1);
        }
    };

    onCopyWeekFieldChange = (name: keyof CopyWeekFormValues, value: string | boolean) => {
        this.setState((currentState) => ({
            copyWeekErrors: {
                ...currentState.copyWeekErrors,
                [name]: undefined,
            },
            copyWeekValues: {
                ...currentState.copyWeekValues,
                [name]: value,
            } as CopyWeekFormValues,
        }));
    };

    onCreateFieldChange = (name: keyof ProgramCreateFormValues, value: string) => {
        this.setState((currentState) => ({
            createErrors: {
                ...currentState.createErrors,
                [name]: undefined,
            },
            createValues: name === 'name'
                ? syncProgramCreateValuesWithName(currentState.createValues, value)
                : {
                    ...currentState.createValues,
                    [name]: value,
                },
        }));
    };

    onCreateDisplayNameChange = (value: string) => {
        this.setState((currentState) => ({
            createErrors: {
                ...currentState.createErrors,
                displayName: undefined,
                name: undefined,
            },
            createValues: syncProgramCreateValuesWithDisplayName(currentState.createValues, value),
        }));
    };

    onCreateBooleanFieldChange = (name: 'live', value: boolean) => {
        this.setState((currentState) => ({
            createValues: {
                ...currentState.createValues,
                [name]: value,
            },
        }));
    };

    onImportProgram = (name: string, weeks: ProgramWeeks) => {
        this.props.startImportedProgramDraft(name, weeks);
        this.setState({ showImportDialog: false });
    };

    onDeleteDay = (day: number) => {
        this.props.removeDraftProgramDay(this.props.selectedWeek, day);
    };

    onDiscardChanges = () => {
        if (this.isNewProgramRoute()) {
            this.props.clearProgramDraft();
            this.setState({
                createErrors: {},
                createValues: DEFAULT_PROGRAM_CREATE_VALUES,
            });
            return;
        }

        this.props.discardDraftProgramChanges();
    };

    onEditDay = (day: number) => {
        this.setState({ activeDay: day, showDayEditor: true });
    };

    onOpenCopyWeekDialog = () => {
        if (!this.props.draftProgram) {
            return;
        }

        const nextWeek = getProgramNextWeekNumber(this.props.draftProgram.weeks);

        this.setState({
            copyWeekErrors: {},
            copyWeekValues: {
                overwriteExisting: false,
                sourceWeek: String(this.props.selectedWeek),
                targetWeek: String(nextWeek),
            },
            showCopyWeekDialog: true,
        });
    };

    onOpenImportDialog = () => {
        this.setState({ showImportDialog: true });
    };

    onUpdateDraftProgramCatalogField = (fieldName: 'displayName', value: string) => {
        this.props.updateDraftProgramCatalog(fieldName, value);
    };

    onUpdateDraftProgramCatalogLive = (fieldName: 'live', value: boolean) => {
        this.props.updateDraftProgramCatalog(fieldName, value);
    };

    onUpdateDraftProgramCatalogWeeks = (value: string) => {
        this.props.updateDraftProgramCatalog('weeks', Number(value));
    };

    onSaveDay = (item: ProgramWorkoutItemRecord) => {
        this.props.updateDraftProgramDay(this.props.selectedWeek, item.day, item);
        this.setState({ activeDay: undefined, showDayEditor: false });
    };

    onSaveProgram = async () => {
        const result = await this.props.saveDraftProgram();

        if (result.success && result.item && this.isNewProgramRoute()) {
            this.props.navigate(`/programs/${result.item.name}/edit`);
        }
    };

    onSelectWeek = (week: number) => {
        this.props.setProgramSelectedWeek(week);
    };

    navigateAfterCancel() {
        if (this.isNewProgramRoute()) {
            this.props.navigate('/programs');
            return;
        }

        this.props.navigate(`/programs/${this.getProgramName()}`);
    }

    render() {
        const { draftProgram, loadingList, loadingProgram, savingProgram, selectedWeek } = this.props;

        if (!this.isNewProgramRoute() && loadingProgram && !draftProgram) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!this.isNewProgramRoute() && !draftProgram) {
            return <Typography variant="h6">Program not found</Typography>;
        }

        if (this.isNewProgramRoute() && !draftProgram) {
            return (
                <>
                    <Box maxWidth={900}>
                        <Paper sx={{ borderRadius: 3, p: { md: 4, xs: 2.5 } }} variant="outlined">
                            <Typography sx={{ fontSize: { md: '1.6rem', xs: '1.4rem' }, fontWeight: 700, mb: 2 }} variant="h4">Create New Program</Typography>
                            <Box sx={{ display: 'grid', gap: 2.5 }}>
                                <ProgramCatalogFields
                                    errors={this.state.createErrors}
                                    hideProgramKey
                                    keyError={this.state.createErrors.name}
                                    onCatalogBooleanChange={this.onCreateBooleanFieldChange}
                                    onCatalogFieldChange={(_, value) => this.onCreateDisplayNameChange(value)}
                                    onCatalogWeeksChange={(value) => this.onCreateFieldChange('weeks', value)}
                                    programCatalog={{
                                        displayName: this.state.createValues.displayName,
                                        live: this.state.createValues.live,
                                        weeks: Number(this.state.createValues.weeks) || 1,
                                    }}
                                    programKey={this.state.createValues.name}
                                    title="Program Catalog"
                                />
                                <Box>
                                    <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle1">Choose Your Starting Point</Typography>
                                    <RadioGroup value={this.state.createValues.templateType} onChange={(event: ChangeEvent<HTMLInputElement>) => this.onCreateFieldChange('templateType', event.target.value)}>
                                        <FormControlLabel control={<Radio />} label="Start Blank" value="blank" />
                                        <FormControlLabel control={<Radio />} label="Copy Existing Program" value="copyProgram" />
                                        <FormControlLabel control={<Radio />} label="Start With One Copied Week" value="copyWeek" />
                                    </RadioGroup>
                                </Box>
                                {this.state.createValues.templateType !== 'blank' && (
                                    <TextField
                                        error={Boolean(this.state.createErrors.sourceProgramName)}
                                        helperText={this.state.createErrors.sourceProgramName}
                                        label="Source Program"
                                        onChange={(event) => this.onCreateFieldChange('sourceProgramName', event.target.value)}
                                        select
                                        size="small"
                                        value={this.state.createValues.sourceProgramName}
                                    >
                                        {this.props.items.map((item) => (
                                            <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {this.state.createValues.templateType === 'copyWeek' && (
                                    <TextField
                                        error={Boolean(this.state.createErrors.sourceWeek)}
                                        helperText={this.state.createErrors.sourceWeek}
                                        label="Source Week"
                                        onChange={(event) => this.onCreateFieldChange('sourceWeek', event.target.value)}
                                        select
                                        size="small"
                                        value={this.state.createValues.sourceWeek}
                                    >
                                        {this.getCreateSourceWeekOptions().map((week) => (
                                            <MenuItem key={week} value={String(week)}>Week {week}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {loadingList && <Alert severity="info">Loading available programs...</Alert>}
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button onClick={this.onOpenImportDialog} variant="text">Import CSV/TSV</Button>
                                    <Button onClick={this.onCancel} variant="outlined">Cancel</Button>
                                    <Button onClick={this.onContinueToBuilder} variant="contained">Continue to Builder</Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <ProgramImportDialog
                        existingProgramNames={this.props.items.map((item) => item.name)}
                        onCancel={this.onCloseImportDialog}
                        onImport={this.onImportProgram}
                        visible={this.state.showImportDialog}
                    />
                </>
            );
        }

        const weekStatistics = getProgramWeekStatistics(draftProgram!.weeks, selectedWeek);
        const weekOptions = createProgramWeekOptions(draftProgram!.weeks);
        const emptyDayCount = weekStatistics.totalDays - weekStatistics.filledDays;

        return (
            <Box maxWidth={1280}>
                <ProgramEditorHeader
                    hasUnsavedChanges={this.props.hasUnsavedChanges}
                    name={draftProgram!.name}
                    onCancel={this.onCancel}
                    onDiscard={this.onDiscardChanges}
                    onSave={this.onSaveProgram}
                    savingProgram={savingProgram}
                />
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { lg: '280px minmax(0, 1fr)', xs: 'minmax(0, 1fr)' } }}>
                    <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                        <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="subtitle1">Weeks</Typography>
                        <ProgramWeekNavigator onAddWeek={this.onAddWeek} onSelectWeek={this.onSelectWeek} selectedWeek={selectedWeek} variant="rail" weeks={draftProgram!.weeks} />
                    </Paper>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <ProgramCatalogFields
                            disabledKey
                            onCatalogBooleanChange={this.onUpdateDraftProgramCatalogLive}
                            onCatalogFieldChange={this.onUpdateDraftProgramCatalogField}
                            onCatalogWeeksChange={this.onUpdateDraftProgramCatalogWeeks}
                            programCatalog={draftProgram!.catalog}
                            programKey={draftProgram!.name}
                        />
                        <Paper sx={{ borderRadius: 3, p: 2 }} variant="outlined">
                            <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }} variant="h6">Week {selectedWeek} Builder</Typography>
                                    <Typography color="text.secondary" variant="body2">Filled days: {weekStatistics.filledDays} / {weekStatistics.totalDays} • Empty days: {emptyDayCount}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Button disabled={!emptyDayCount} onClick={this.onAddDayFromWeekSummary} variant="contained">Add Day</Button>
                                    <Button onClick={this.onOpenCopyWeekDialog} variant="outlined">Copy Week</Button>
                                </Box>
                            </Box>
                        </Paper>
                        <ProgramWeekGrid
                            onAddDay={this.onAddDay}
                            onDeleteDay={this.onDeleteDay}
                            onEditDay={this.onEditDay}
                            selectedWeek={selectedWeek}
                            variant="builder"
                            weeks={draftProgram!.weeks}
                        />
                    </Box>
                </Box>
                <ProgramDayEditorModal
                    day={this.state.activeDay || 1}
                    initialItem={this.getActiveDayItem()}
                    mode={this.getActiveDayItem() ? 'edit' : 'create'}
                    onCancel={this.onCloseDayEditor}
                    onSave={this.onSaveDay}
                    visible={this.state.showDayEditor}
                    week={selectedWeek}
                />
                <CopyWeekDialog
                    errors={this.state.copyWeekErrors}
                    onCancel={this.onCloseCopyWeekDialog}
                    onConfirm={this.onConfirmCopyWeek}
                    onFieldChange={this.onCopyWeekFieldChange}
                    sourceConfiguredDays={Object.keys(getProgramWeekSchedule(draftProgram!.weeks, Number(this.state.copyWeekValues.sourceWeek))).length}
                    targetConfiguredDays={Object.keys(getProgramWeekSchedule(draftProgram!.weeks, Number(this.state.copyWeekValues.targetWeek))).length}
                    values={this.state.copyWeekValues}
                    visible={this.state.showCopyWeekDialog}
                    weekOptions={weekOptions}
                />
                <UnsavedChangesDialog onCancel={this.onCloseUnsavedChangesDialog} onConfirm={this.onConfirmDiscardChanges} visible={this.state.showUnsavedChangesDialog} />
                <ProgramImportDialog
                    existingProgramNames={this.props.items.map((item) => item.name)}
                    onCancel={this.onCloseImportDialog}
                    onImport={this.onImportProgram}
                    visible={this.state.showImportDialog}
                />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ programs }: ReduxState) => ({
    draftProgram: programs.draftProgram,
    hasUnsavedChanges: programs.hasUnsavedChanges,
    items: programs.items,
    loadingList: programs.loadingList,
    loadingProgram: programs.loadingProgram,
    savingProgram: programs.savingProgram,
    selectedWeek: programs.selectedWeek,
});

const mapDispatchToProps: any = {
    addProgramDraftWeek,
    clearProgramDraft,
    copyDraftProgramWeek,
    discardDraftProgramChanges,
    loadProgramEditor,
    loadPrograms,
    removeDraftProgramDay,
    saveDraftProgram,
    setProgramSelectedWeek,
    startImportedProgramDraft,
    startNewProgramDraft,
    updateDraftProgramCatalog,
    updateDraftProgramDay,
};

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, mapDispatchToProps)(withRouter(EditProgramPage));